import type {
  Affiliate,
  AffiliateDetail,
  AffiliateListItem,
  AffiliateUpdateInput,
  AuditLog,
  ChangePasswordInput,
  DailyReport,
  DashboardStats,
  Import,
  ImportPreview,
  ImportResult,
  ImportRow,
  MailboxConnectionInput,
  MailboxStatus,
  MailboxTest,
  MonthlyReport,
  PermissionKey,
  ProductListItem,
  Role,
  Session,
  SessionUser,
  Settings,
  SettingsInput,
  UpdateProfileInput,
  User,
  UserCreateInput,
  UserListItem,
  UserResetPasswordInput,
  UserRole,
  UserUpdateInput,
} from '@afiliados/shared';
import { api } from './client';

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export type Query = Record<string, string | number | boolean | undefined | null>;

export const authApi = {
  login: (email: string, password: string) =>
    api<{ accessToken: string; user: SessionUser }>('/api/auth/login', {
      method: 'POST',
      body: { email, password },
    }),
  logout: () => api<void>('/api/auth/logout', { method: 'POST' }),
  me: () => api<SessionUser>('/api/auth/me'),
  updateProfile: (body: UpdateProfileInput) =>
    api<SessionUser>('/api/auth/me', { method: 'PATCH', body }),
  changePassword: (body: ChangePasswordInput) =>
    api<SessionUser>('/api/auth/change-password', { method: 'POST', body }),
  sessions: () => api<Session[]>('/api/auth/sessions'),
  revokeSession: (id: string) => api<void>(`/api/auth/sessions/${id}`, { method: 'DELETE' }),
  revokeOtherSessions: () => api<{ revoked: number }>('/api/auth/sessions', { method: 'DELETE' }),
};

export const usersApi = {
  list: (search?: string) => api<UserListItem[]>('/api/users', { query: { search } }),
  get: (id: string) => api<{ user: User; sessions: Session[] }>(`/api/users/${id}`),
  create: (body: UserCreateInput) => api<User>('/api/users', { method: 'POST', body }),
  update: (id: string, body: UserUpdateInput) =>
    api<User>(`/api/users/${id}`, { method: 'PATCH', body }),
  resetPassword: (id: string, body: UserResetPasswordInput) =>
    api<User>(`/api/users/${id}/reset-password`, { method: 'POST', body }),
  unlock: (id: string) => api<User>(`/api/users/${id}/unlock`, { method: 'POST' }),
  revokeSessions: (id: string) =>
    api<{ revoked: number }>(`/api/users/${id}/sessions`, { method: 'DELETE' }),
  remove: (id: string) => api<void>(`/api/users/${id}`, { method: 'DELETE' }),
};

export interface PermissionInfo {
  key: PermissionKey;
  feature: string;
  description: string;
}
export const rolesApi = {
  list: () => api<Role[]>('/api/roles'),
  permissions: () => api<PermissionInfo[]>('/api/roles/permissions'),
  update: (role: UserRole, permissions: PermissionKey[]) =>
    api<Role>(`/api/roles/${role}/permissions`, { method: 'PUT', body: { permissions } }),
};

export const auditApi = {
  list: (query: Query) => api<Paginated<AuditLog>>('/api/audit', { query }),
};

export const dashboardApi = {
  get: (query: Query) => api<DashboardStats>('/api/dashboard', { query }),
};

export const reportsApi = {
  months: () => api<string[]>('/api/reports/months'),
  monthly: (query: Query) => api<MonthlyReport>('/api/reports/monthly', { query }),
  daily: (query: Query) => api<DailyReport>('/api/reports/daily', { query }),
  /** Baixa o Excel e dispara o download no navegador. */
  download: async (path: 'monthly' | 'daily', query: Query, fallbackName: string) => {
    const res = await api<Response>(`/api/reports/${path}/export`, { query, raw: true });
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') ?? '';
    const name = /filename="([^"]+)"/.exec(cd)?.[1] ?? fallbackName;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  },
};

export const affiliatesApi = {
  list: (query: Query) => api<Paginated<AffiliateListItem>>('/api/affiliates', { query }),
  search: (q: string) => api<Affiliate[]>('/api/affiliates/search', { query: { q } }),
  get: (id: string, query: Query = {}) => api<AffiliateDetail>(`/api/affiliates/${id}`, { query }),
  update: (id: string, body: AffiliateUpdateInput) =>
    api<Affiliate>(`/api/affiliates/${id}`, { method: 'PATCH', body }),
  merge: (id: string, sourceId: string) =>
    api<Affiliate>(`/api/affiliates/${id}/merge`, { method: 'POST', body: { sourceId } }),
};

export const productsApi = {
  list: (query: Query = {}) => api<ProductListItem[]>('/api/products', { query }),
};

export interface UploadFields {
  replace?: boolean;
  periodStart?: string;
  periodEnd?: string;
}

function uploadForm(file: File, fields: UploadFields) {
  const fd = new FormData();
  for (const [k, v] of Object.entries(fields)) {
    if (v !== undefined && v !== '') fd.append(k, String(v));
  }
  fd.append('file', file);
  return fd;
}

export const importsApi = {
  list: (query: Query) => api<Paginated<Import>>('/api/imports', { query }),
  get: (id: string) => api<Import>(`/api/imports/${id}`),
  rows: (id: string, query: Query) =>
    api<Paginated<ImportRow>>(`/api/imports/${id}/rows`, { query }),
  preview: (file: File, fields: UploadFields = {}) =>
    api<ImportPreview>('/api/imports/preview', { method: 'POST', body: uploadForm(file, fields) }),
  create: (file: File, fields: UploadFields = {}) =>
    api<ImportResult>('/api/imports', { method: 'POST', body: uploadForm(file, fields) }),
  activate: (id: string) => api<ImportResult>(`/api/imports/${id}/activate`, { method: 'POST' }),
  remove: (id: string) => api<void>(`/api/imports/${id}`, { method: 'DELETE' }),
  fileUrl: (id: string) => `${import.meta.env.VITE_API_URL ?? ''}/api/imports/${id}/file`,
  downloadFile: async (id: string, fileName: string) => {
    const res = await api<Response>(`/api/imports/${id}/file`, { raw: true });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  },
};

export const settingsApi = {
  get: () => api<Settings>('/api/settings'),
  update: (body: SettingsInput) => api<Settings>('/api/settings', { method: 'PUT', body }),
  clearMailboxPassword: () => api<Settings>('/api/settings/mailbox/password', { method: 'DELETE' }),
  mailbox: () => api<MailboxStatus>('/api/settings/mailbox'),
  mailboxTest: (body: MailboxConnectionInput) =>
    api<MailboxTest>('/api/settings/mailbox/test', { method: 'POST', body }),
  mailboxRun: () => api<MailboxStatus>('/api/settings/mailbox/run', { method: 'POST' }),
  mailboxReset: () => api<MailboxStatus>('/api/settings/mailbox/reset', { method: 'POST' }),
};
